// src/components/shared/forms/UniversalField.jsx
import React, { memo } from 'react';
import { FormField, TextInput, TextArea, SelectInput, Toggle } from './FormComponents';
import { FIELD_TYPES, getFieldIcon } from './FieldTypes';
import { ImageUpload } from './ImageUpload';
import { RichTextEditor } from './RichTextEditor';
import { AddressInput } from './specialised/AddressInput';
import { CurrencyInput } from './specialised/CurrencyInput';
import { DynamicListInput } from './DynamicListInput';

const UniversalField = memo(({ 
  field, 
  value, 
  onChange, 
  error, 
  disabled = false,
  className = "",
  templateType = "default",
  ...props 
}) => {
  const {
    type = FIELD_TYPES.TEXT,
    label,
    placeholder,
    description,
    required = false,
    validation = {},
    options = [],
    maxSize,
    folder,
    rows = 3,
    min,
    max,
    step,
    multiple = false,
    accept,
    itemTemplate,
    addLabel = "Add Item",
    showIcon = true,
    ...fieldConfig
  } = field;

  const FieldIcon = showIcon ? getFieldIcon(type) : null;

  const renderFieldInput = () => {
    const commonProps = {
      value: value || '',
      onChange,
      placeholder,
      error,
      disabled,
      ...props
    };

    switch (type) {
      case FIELD_TYPES.TEXTAREA:
        return <TextArea {...commonProps} rows={rows} />;
      
      case FIELD_TYPES.SELECT:
      case FIELD_TYPES.MULTISELECT:
        return (
          <SelectInput 
            {...commonProps} 
            options={options} 
            multiple={type === FIELD_TYPES.MULTISELECT || multiple} 
          />
        );
      
      case FIELD_TYPES.TOGGLE:
        return (
          <Toggle
            checked={!!value}
            onChange={onChange}
            label={label}
            description={description}
            disabled={disabled}
          />
        );
      
      case FIELD_TYPES.IMAGE:
        return (
          <ImageUpload
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            maxSize={maxSize || 5}
            folder={folder || 'general'}
            accept={accept || "image/*"}
            disabled={disabled}
          />
        );
      
      case FIELD_TYPES.RICH_TEXT:
        return (
          <RichTextEditor
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      
      case FIELD_TYPES.LIST:
        return (
          <DynamicListInput
            value={value || []}
            onChange={onChange}
            itemTemplate={itemTemplate || {}}
            addLabel={addLabel}
            disabled={disabled}
          />
        );
      
      case FIELD_TYPES.ADDRESS:
        return (
          <AddressInput
            value={value || {}}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      
      case FIELD_TYPES.CURRENCY:
        return (
          <CurrencyInput
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
          />
        );
      
      default:
        return (
          <TextInput
            {...commonProps}
            type={type}
            min={min}
            max={max}
            step={step}
          />
        );
    }
  };

  // Skip FormField wrapper for toggle (handles its own label)
  if (type === FIELD_TYPES.TOGGLE) {
    return <div className={className}>{renderFieldInput()}</div>;
  }

  return (
    <FormField
      label={
        FieldIcon ? (
          <div className="flex items-center gap-2">
            <FieldIcon className="h-4 w-4 text-gray-500" />
            {label}
          </div>
        ) : label
      }
      required={required}
      error={error}
      description={description}
      className={className}
    >
      {renderFieldInput()}
    </FormField>
  );
});

UniversalField.displayName = 'UniversalField';
export default UniversalField;